import React, { useState, useEffect } from 'react';
import { pathsAPI } from '../api/pathsAPI';
import '../../onboarding/components/OnboardingOptionCard.css';
import '../../onboarding/components/OnboardingSummaryCard.css';
import './ReviewCheckpointCard.css';

// Review checkpoint UI (spec §4.8, §3.9, §5.20). Built to the same
// "payload + callbacks" contract as DraftPlanCard, with one deliberate
// exception: it fetches its own execution_summary preview on mount (via
// pathsAPI.getReviewPreview — a GET, no write) since that summary is
// computed server-side from live progress data at the moment the card
// opens, not something the caller can hand in as a static prop the way
// DraftPlanCard's version/items are. Submission itself (onSubmit) still
// makes no direct network call — it's a callback, consistent with the
// contract's "zero network calls for the actual decision" spirit; only the
// read-only preview fetch is local to this component.
//
// item: { id, title, instruction }
// onSubmit(decision, reflection, reportedOutcome) — decision is
//   'continue' | 'revise'.
// submitting: bool

function ExecutionSummaryList({ label, items, emptyText }) {
  if (!items || items.length === 0) {
    return (
      <div className="review-summary-row">
        <div className="onboarding-eyebrow">{label}</div>
        <p className="review-summary-empty">{emptyText}</p>
      </div>
    );
  }
  return (
    <div className="review-summary-row">
      <div className="onboarding-eyebrow">{label}</div>
      <ul className="review-summary-list">
        {items.map((item) => <li key={item.id}>{item.title}</li>)}
      </ul>
    </div>
  );
}

export default function ReviewCheckpointCard({ item, onSubmit, submitting }) {
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const [reflection, setReflection] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function loadPreview() {
      setLoadingSummary(true);
      setSummaryError(null);
      try {
        const data = await pathsAPI.getReviewPreview(item.id);
        if (!cancelled) setSummary(data);
      } catch (e) {
        if (!cancelled) setSummaryError(e?.message || 'Failed to load review summary.');
      } finally {
        if (!cancelled) setLoadingSummary(false);
      }
    }
    loadPreview();
    return () => { cancelled = true; };
  }, [item.id]);

  const handleContinue = () => onSubmit('continue', reflection || null, null);
  const handleRevise = () => onSubmit('revise', reflection || null, null);

  return (
    <div className="onboarding-card review-checkpoint-card">
      <div className="review-checkpoint-header">
        <div className="onboarding-eyebrow">Review point</div>
        <h3 className="review-checkpoint-title">{item.title}</h3>
        {item.instruction && <p className="review-checkpoint-instruction">{item.instruction}</p>}
      </div>

      {loadingSummary && <p className="review-summary-loading">Loading summary…</p>}
      {summaryError && <p className="review-summary-loading">{summaryError}</p>}

      {summary && (
        <div className="review-checkpoint-summary">
          {/* Factual counts only — never a goal-progress percentage, per
              the same house style PathView follows (spec
              §3.7). */}
          <p className="review-summary-counts">
            {summary.counts.completed} of {summary.counts.total} planned actions completed
            {summary.counts.skipped > 0 && ` (${summary.counts.skipped} skipped)`}
          </p>
          <ExecutionSummaryList label="Completed" items={summary.completed} emptyText="Nothing completed yet." />
          <ExecutionSummaryList label="Skipped" items={summary.skipped} emptyText="Nothing skipped." />
          <ExecutionSummaryList label="Still pending" items={summary.pending} emptyText="Nothing pending." />
        </div>
      )}

      <div className="review-checkpoint-reflection">
        <div className="onboarding-eyebrow">Reflection (optional)</div>
        <textarea
          className="review-checkpoint-reflection-input"
          value={reflection}
          onChange={(e) => setReflection(e.target.value)}
          placeholder="How did it go? Anything you'd like to change?"
          disabled={submitting}
        />
      </div>

      <div className="onboarding-card-foot onboarding-card-foot-readonly">
        <button type="button" className="onboarding-btn-quiet" onClick={handleRevise} disabled={submitting}>
          {submitting ? 'Saving…' : 'I want to adjust the plan'}
        </button>
        <button type="button" className="onboarding-btn-primary" onClick={handleContinue} disabled={submitting}>
          {submitting ? 'Saving…' : 'Continue as planned'}
        </button>
      </div>
    </div>
  );
}
